import { ICustomerDetail, ISetCustomerData } from "@interfaces";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { redeemerApi } from "src/Api/redeemers";
import { RootState } from ".";
import _ from "lodash";

interface RedeemerState {
    redeemer: ICustomerDetail[];
    bookNowRedeemer: ICustomerDetail[];
    userRedeemers: ICustomerDetail[];
    loading: boolean;
    bookNowLoading: boolean;
    userRedeemersLoading: boolean;
    error: string | null;
    bookNowError: string | null;
    userRedeemersError: string | null;
}

const initialState: RedeemerState = {
    redeemer: [],
    bookNowRedeemer: [],
    userRedeemers: [],
    loading: false,
    bookNowLoading: false,
    userRedeemersLoading: false,
    error: null,
    bookNowError: null,
    userRedeemersError: null,
};

export const fetchRedeemer = createAsyncThunk<ICustomerDetail[], string | undefined>(
    "redeemer/fetchRedeemer",
    async (sessionId, { rejectWithValue, getState }) => {
        try {
            const tempJwt = sessionStorage.getItem("tempJwt");
            if (tempJwt && !sessionId) {
                return [];
            }

            const state = getState() as RootState;
            const isAuthenticated = !_.isNil(state.auth.jwt);

            if (isAuthenticated) {
                try {
                    const response = await redeemerApi.getUserRedeemers();
                    return response.data.data as ICustomerDetail[];
                } catch {
                    const response = await redeemerApi.getRedeemer(sessionId);
                    return response.data.data as ICustomerDetail[];
                }
            }

            const response = await redeemerApi.getRedeemer(sessionId);
            return response.data.data as ICustomerDetail[];
        } catch (error) {
            console.error("Failed to fetch redeemers:", error);
            return rejectWithValue("Unable to fetch redeemer data.");
        }
    },
);

export const createNewRedeemer = createAsyncThunk<
    ICustomerDetail,
    { customerData: ISetCustomerData; sessionId?: string }
>("customer/createNewRedeemer", async ({ customerData, sessionId }, { rejectWithValue }) => {
    try {
        const createResponse = await redeemerApi.AddRedeemer(customerData, sessionId);
        return createResponse.data.data as ICustomerDetail;
    } catch (error: any) {
        console.error("Failed to update customer or fetch customer data:", error);
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const updateRedeemer = createAsyncThunk<ICustomerDetail[], ISetCustomerData[]>(
    "redeemer/updateRedeemer",
    async (params, { rejectWithValue }) => {
        try {
            const response = await redeemerApi.updateRedeemer(params);
            return response.data.data as ICustomerDetail[];
        } catch (error: any) {
            console.error("Failed to update redeemers:", error);
            return rejectWithValue(error.response?.data || error.message);
        }
    },
);

export const fetchBookNowRedeemer = createAsyncThunk<ICustomerDetail[], string | undefined>(
    "redeemer/fetchBookNowRedeemer",
    async (sessionId, { rejectWithValue, getState }) => {
        try {
            const tempJwt = sessionStorage.getItem("tempJwt");
            if (tempJwt && !sessionId) {
                return [];
            }

            const state = getState() as RootState;
            const isAuthenticated = !_.isNil(state.auth.jwt);

            if (isAuthenticated) {
                try {
                    const response = await redeemerApi.getUserRedeemers();
                    return response.data.data as ICustomerDetail[];
                } catch {
                    const response = await redeemerApi.getRedeemerBookNow(sessionId);
                    return response.data.data as ICustomerDetail[];
                }
            }

            const response = await redeemerApi.getRedeemerBookNow(sessionId);
            return response.data.data as ICustomerDetail[];
        } catch (error) {
            console.error("Failed to fetch BookNow redeemers:", error);
            return rejectWithValue("Unable to fetch BookNow redeemer data.");
        }
    },
);

export const createNewBookNowRedeemer = createAsyncThunk<
    ICustomerDetail,
    { customerData: ISetCustomerData; sessionId?: string }
>("customer/createNewBookNowRedeemer", async ({ customerData, sessionId }, { rejectWithValue }) => {
    try {
        const createResponse = await redeemerApi.AddRedeemerBookNow(customerData, sessionId);
        return createResponse.data.data as ICustomerDetail;
    } catch (error: any) {
        console.error("Failed to create BookNow redeemer:", error);
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const updateBookNowRedeemer = createAsyncThunk<ICustomerDetail[], ISetCustomerData[]>(
    "redeemer/updateBookNowRedeemer",
    async (params, { rejectWithValue }) => {
        try {
            const response = await redeemerApi.updateRedeemerBookNow(params);
            return response.data.data as ICustomerDetail[];
        } catch (error: any) {
            console.error("Failed to update BookNow redeemers:", error);
            return rejectWithValue(error.response?.data || error.message);
        }
    },
);

// Fetches all redeemers linked to the authenticated user (bypasses session filtering)
// Used for traveller suggestions in checkout to show all previously added travellers
export const fetchUserRedeemers = createAsyncThunk<ICustomerDetail[], string | undefined>(
    "redeemer/fetchUserRedeemers",
    async (sessionId, { rejectWithValue, getState }) => {
        try {
            const state = getState() as RootState;
            const isAuthenticated = !_.isNil(state.auth.jwt);

            if (!isAuthenticated) {
                return [];
            }

            try {
                const response = await redeemerApi.getUserRedeemers();
                return response.data.data as ICustomerDetail[];
            } catch {
                const response = await redeemerApi.getRedeemer(sessionId);
                return response.data.data as ICustomerDetail[];
            }
        } catch (error) {
            console.error("Failed to fetch user redeemers:", error);
            return rejectWithValue("Unable to fetch user redeemer data.");
        }
    },
);

const redeemerSlice = createSlice({
    name: "redeemer",
    initialState,
    reducers: {
        clearRedeemerErrors: (state) => {
            state.error = null;
            state.bookNowError = null;
            state.userRedeemersError = null;
        },
        clearBookNowRedeemer: (state) => {
            state.bookNowRedeemer = [];
        },
        clearUserRedeemers: (state) => {
            state.userRedeemers = [];
        },
    },
    extraReducers: (builder) => {
        builder
            // Regular redeemer cases
            .addCase(fetchRedeemer.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchRedeemer.fulfilled, (state, action) => {
                state.loading = false;
                state.redeemer = action.payload;
            })
            .addCase(fetchRedeemer.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(createNewRedeemer.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createNewRedeemer.fulfilled, (state, action) => {
                state.loading = false;
                state.redeemer.push(action.payload);
                // Also add to userRedeemers for suggestions
                state.userRedeemers.push(action.payload);
            })
            .addCase(createNewRedeemer.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(updateRedeemer.fulfilled, (state, action) => {
                state.redeemer = action.payload;
            })
            .addCase(updateRedeemer.rejected, (state, action) => {
                state.error = action.payload as string;
            })
            // BookNow redeemer cases
            .addCase(fetchBookNowRedeemer.pending, (state) => {
                state.bookNowLoading = true;
                state.bookNowError = null;
            })
            .addCase(fetchBookNowRedeemer.fulfilled, (state, action) => {
                state.bookNowLoading = false;
                state.bookNowRedeemer = action.payload;
            })
            .addCase(fetchBookNowRedeemer.rejected, (state, action) => {
                state.bookNowLoading = false;
                state.bookNowError = action.payload as string;
            })
            .addCase(createNewBookNowRedeemer.pending, (state) => {
                state.bookNowLoading = true;
                state.bookNowError = null;
            })
            .addCase(createNewBookNowRedeemer.fulfilled, (state, action) => {
                state.bookNowLoading = false;
                state.bookNowRedeemer.push(action.payload);
                // Also add to userRedeemers for suggestions
                state.userRedeemers.push(action.payload);
            })
            .addCase(createNewBookNowRedeemer.rejected, (state, action) => {
                state.bookNowLoading = false;
                state.bookNowError = action.payload as string;
            })
            .addCase(updateBookNowRedeemer.fulfilled, (state, action) => {
                state.bookNowRedeemer = action.payload;
            })
            .addCase(updateBookNowRedeemer.rejected, (state, action) => {
                state.bookNowError = action.payload as string;
            })
            // User redeemers cases (all redeemers for authenticated user)
            .addCase(fetchUserRedeemers.pending, (state) => {
                state.userRedeemersLoading = true;
                state.userRedeemersError = null;
            })
            .addCase(fetchUserRedeemers.fulfilled, (state, action) => {
                state.userRedeemersLoading = false;
                state.userRedeemers = action.payload;
            })
            .addCase(fetchUserRedeemers.rejected, (state, action) => {
                state.userRedeemersLoading = false;
                state.userRedeemersError = action.payload as string;
            });
    },
});

export const { clearRedeemerErrors, clearBookNowRedeemer, clearUserRedeemers } =
    redeemerSlice.actions;

export const selectRedeemer = (state: RootState) => state.redeemer.redeemer;
export const selectBookNowRedeemer = (state: RootState) => state.redeemer.bookNowRedeemer;
export const selectUserRedeemers = (state: RootState) => state.redeemer.userRedeemers;
export const selectRedeemerLoading = (state: RootState) => state.redeemer.loading;
export const selectBookNowRedeemerLoading = (state: RootState) => state.redeemer.bookNowLoading;
export const selectUserRedeemersLoading = (state: RootState) => state.redeemer.userRedeemersLoading;
export const selectRedeemerError = (state: RootState) => state.redeemer.error;
export const selectBookNowRedeemerError = (state: RootState) => state.redeemer.bookNowError;
export const selectUserRedeemersError = (state: RootState) => state.redeemer.userRedeemersError;

export default redeemerSlice.reducer;
